import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation as faTriangleExclamationRegular } from "@fortawesome/pro-regular-svg-icons/faTriangleExclamation";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { Dialog, DialogActions, DialogContent, Grid, Tab } from "@mui/material";
import { AxiosResponse, GenericAbortSignal } from "axios";
import { MprcBarTabs, MprcConfirmDialog, MprcContainer, MprcDialogTitleClose, MprcIconFa, MprcJoinComponents, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcNotice } from "components/MprcNotice";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { CreateOrUpdateMeetingComponentCommand, ManagedMeetingDto, MeetingDefaultResponse, MeetingInstructorConflitsResponse, MeetingType } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import LibUtils from "lib/LibUtils";
import TzUtils from "lib/TzUtils";
import moment from "moment";
import { useSnackbar } from "notistack";
import { CoreApiContextType, isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NIL } from "uuid";
import { EditMeetingInstructorBox } from "views/lms/meeting/creation/EditMeetingInstructorBox";
import { EditMeeetingMainBoxSkeleton, EditMeetingMainBox } from "views/lms/meeting/creation/EditMeetingMainBox";
import { EditMeetingPresenceBox } from "views/lms/meeting/creation/EditMeetingPresenceBox";
import { EditMeetingWhenBox } from "views/lms/meeting/creation/EditMeetingWhenBox";

////////////////////////////////////////////////////////////////////////////////

type CourseEventTapOptions = "when" | "instructors" | "score";

export type EditMeetingMode = "classroom" | "training";

interface IBoxValidations {
    mainBox?: boolean,
    whenBox?: boolean,
    instructorsBox?: boolean,
    scoreBox?: boolean,
}

interface ICourseEventTab {
    value: CourseEventTapOptions,
    name: string,
    hasError: boolean;
}

interface IPros {
    mode: EditMeetingMode;
    meetingId: string | null;
    meetingType: MeetingType;
    trainingId?: string;
    hasCloseEnrollments: boolean;
    replacementComponentId?: string | null;
    onClose: (success: boolean) => void;
    saveApi: (data: CreateOrUpdateMeetingComponentCommand) => Promise<AxiosResponse<boolean, any>>;
    onGetData: (meetingId: string, replacementComponentId?: string | null) => Promise<AxiosResponse<ManagedMeetingDto, any>>;
}

/////////////////////////////////////////////////////////////////////////////////

export const getMeetingTimezoneId = (dto: ManagedMeetingDto): string | null => {
    return (dto.meetingType == MeetingType.Presential
        ? dto.meetingRoom ? dto.meetingRoom.timezoneId : dto.location?.timezoneId ?? null
        : dto.onlineLocation?.timezoneId) ?? null;
};

export async function getInstructorsConflicts(
    api: CoreApiContextType,
    viewData: ManagedMeetingDto,
    signal: GenericAbortSignal | undefined,
    onError: (e: any) => void): Promise<MeetingInstructorConflitsResponse[]> {

    const currentTimezoneId = getMeetingTimezoneId(viewData);
    const startDateUtc = currentTimezoneId == null ? viewData.startDate : TzUtils.applyTimezoneOffsetToUtc(viewData.startDate, currentTimezoneId);
    const endDateUtc = currentTimezoneId == null ? viewData.endDate : TzUtils.applyTimezoneOffsetToUtc(viewData.endDate, currentTimezoneId);
    let conflits: MeetingInstructorConflitsResponse[] = [];

    if (viewData.instructors.length > 0 && startDateUtc != null && endDateUtc != null && moment(startDateUtc).isValid() && moment(endDateUtc).isValid()) {
        try {
            const { data } = await api.v1.trainingManagement.getMeetingInstructorConflits({
                meetingId: viewData.meetingId,
                additionalTimeAfter: moment(viewData.bookAdditionalTimeAfter, "HH:mm").isValid() ? viewData.bookAdditionalTimeAfter ?? null : null,
                additionalTimeBefore: moment(viewData.bookAdditionalTimeBefore, "HH:mm").isValid() ? viewData.bookAdditionalTimeBefore ?? null : null,
                instructorIds: viewData.instructors.map(x => x.key),
                startDate: startDateUtc,
                endDate: endDateUtc,
            }, { signal });

            conflits = data.map(item => (currentTimezoneId != null ? {
                ...item,
                startDate: TzUtils.applyTimezoneOffsetFromUtc(item.startDate, currentTimezoneId) ?? item.startDate,
                endDate: TzUtils.applyTimezoneOffsetFromUtc(item.endDate, currentTimezoneId) ?? item.endDate
            } : item));
        }
        catch (e) {
            if (!isCanceledError(e)) onError(e);
        }
    }

    return conflits;
}

export function buildEndDate(dt: Date, diffMilliseconds: number) {
    let endDate: Date;

    if (dt.getHours() == 0 && dt.getMinutes() == 0 && dt.getSeconds() == 0)
        endDate = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 23, 59, 59);
    else {
        var mdt = moment(dt).add(diffMilliseconds, "milliseconds");
        endDate = mdt.toDate();
    }

    return endDate;
}

function getEmptyDto(meetingType: MeetingType, defaultProps?: MeetingDefaultResponse): ManagedMeetingDto {
    const baseDt = LibUtils.getNextHour();
    return {
        meetingId: NIL,
        name: "",
        description: null,
        thumbnail: null,
        assignPresenceAfterViewRecording: false,
        hasScore: false,
        startDate: defaultProps?.multipleClasses ? null : baseDt,
        endDate: defaultProps?.multipleClasses ? null : buildEndDate(baseDt, 3600000),
        bookAdditionalTimeAfter: null,
        bookAdditionalTimeBefore: null,
        instructors: [],
        assistants: [],
        multipleClasses: defaultProps?.multipleClasses ?? false,
        meetingType: meetingType == MeetingType.Presential ? MeetingType.Presential : MeetingType.Other,
        meetingTypesEnabled: defaultProps?.meetingTypesEnabled ?? [MeetingType.Presential, MeetingType.Other],
        location: {
            timezoneId: defaultProps?.locationTimezoneId ?? moment.tz.guess(),
            address: null,
            locationName: ""
        },
        onlineLocation: {
            externalUrl: null,
            teamsUrl: null,
            zoomUrl: null,
            minPermanence: null,
            timezoneId: defaultProps?.locationTimezoneId ?? moment.tz.guess()
        },
        meetingRoom: null,
        classroomName: null,
        trainingName: "",
        started: false,
        computeProgress: true
    };
}

/////////////////////////////////////////////////////////////////////////////////

export const EditMeeting: React.FC<IPros> = (props) => {
    const { mode, meetingId, trainingId, replacementComponentId, hasCloseEnrollments, onGetData, saveApi, onClose } = props;
    const isNewRecord = meetingId == null;

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { enqueueSnackbar } = useSnackbar();
    const abortControllerRef = useRef<AbortController>(undefined);

    const [viewData, setViewData] = useState<ManagedMeetingDto>(() => getEmptyDto(props.meetingType));
    const [loadDto, setLoadDto] = useState<ManagedMeetingDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
    const [viewError, setViewError] = useState<any>();
    const [dialog, setDialog] = useState<any>();
    const [currentTab, setCurrentTab] = useState<CourseEventTapOptions>("when");
    const [instructorConflits, setInstructorConflits] = useState<MeetingInstructorConflitsResponse[]>([]);
    const [boxValidations, setBoxValidations] = useState<IBoxValidations>({
        mainBox: !isNewRecord,
        whenBox: true,
        instructorsBox: true,
        scoreBox: true,
    });

    const [tabs, setTabs] = useState<ICourseEventTab[]>(
        [
            { value: "when", name: t("Meeting.Common.WhenText"), hasError: meetingId == null },
            { value: "instructors", name: t("Meeting.Common.InstructorsText"), hasError: meetingId == null },
            { value: "score", name: t("Meeting.Common.ScoreText"), hasError: false },
        ]
    );

    const disabled = saving || loading;
    const nameRef = useRef<HTMLInputElement | undefined>(undefined);
    const canSave = (recordChanged || replacementComponentId != null) && Object.values(boxValidations).filter((x) => x === false).length == 0;

    /////////////////////////////////////////////////////////////////////////////////

    ///
    const handleSelectTab = (event: React.SyntheticEvent, newValue: CourseEventTapOptions) => {
        setCurrentTab(newValue);
    };

    ///
    const handleUpdateViewData = (newDto: Partial<ManagedMeetingDto>) => {
        setViewData(state => ({ ...state, ...newDto }));
    };

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);
        if (r.fieldsWithError) {
            setViewFieldsErrors(r.fieldsWithError);
            setViewError(r.otherErrors);
            enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
        } else {
            setViewError(e);
        }
    };

    ///
    const updateTabError = (value: CourseEventTapOptions, hasError: boolean) => {
        setTabs(state => (state.map(x => x.value == value ? { ...x, hasError } : x)));
    };

    const buildPostDto = (dto: ManagedMeetingDto): CreateOrUpdateMeetingComponentCommand => {

        var timezoneId: string | null = getMeetingTimezoneId(dto);
        var request: CreateOrUpdateMeetingComponentCommand = {
            meetingId: dto.meetingId,
            name: dto.name,
            description: dto.description,
            thumbnailId: dto.thumbnail?.fileId ?? null,
            assignPresenceAfterViewRecording: dto.assignPresenceAfterViewRecording,
            hasScore: dto.hasScore,
            instructors: dto.instructors.length == 0 ? null : dto.instructors.map(x => x.key),
            assistants: dto.assistants.length == 0 ? null : dto.assistants.map(x => x.key),
            meetingType: dto.meetingType,
            timezoneId: timezoneId,
            startDate: timezoneId == null ? dto.startDate : TzUtils.applyTimezoneOffsetToUtc(dto.startDate, timezoneId),
            endDate: timezoneId == null ? dto.endDate : TzUtils.applyTimezoneOffsetToUtc(dto.endDate, timezoneId),
            bookAdditionalTimeAfter: moment(dto.bookAdditionalTimeAfter, "HH:mm").isValid() ? dto.bookAdditionalTimeAfter : null,
            bookAdditionalTimeBefore: moment(dto.bookAdditionalTimeBefore, "HH:mm").isValid() ? dto.bookAdditionalTimeBefore : null,
            replaceComponentId: replacementComponentId ?? null
        } as CreateOrUpdateMeetingComponentCommand;

        if (dto.meetingType == MeetingType.Presential) {
            request.address = !dto.meetingRoom ? dto.location?.address ?? null : null;
            request.locationName = !dto.meetingRoom ? dto.location?.locationName ?? null : null;
            request.roomId = dto.meetingRoom?.key;
        }
        else {
            if (dto.onlineLocation) {
                request.externalUrl = dto.meetingType == MeetingType.Other ? dto.onlineLocation.externalUrl : null;
                request.minPermanence = dto.onlineLocation.minPermanence;
            }
        }

        return request;
    };


    const handleSave = () => {

        const postData = () => {
            setSaving(true);
            saveApi(buildPostDto(viewData))
                .then(() => {
                    if (isNewRecord)
                        enqueueSnackbar(t("Meeting.Common.SuccessMessage"), { variant: "success" });
                    else
                        enqueueSnackbar(t("Meeting.Common.UpdateSuccessMessage"), { variant: "success" });

                    onClose(true);
                })
                .catch(e => resolveErrors(e))
                .finally(() => setSaving(false));
        };

        if ((isNewRecord || !moment(loadDto?.startDate).isSame(viewData.startDate)) && viewData.startDate != null && viewData.startDate < new Date()) {
            setDialog(
                <MprcConfirmDialog
                    open
                    message={isNewRecord
                        ? t("Meeting.Common.RetroativeEventNewConfirmMessage")
                        : t("Meeting.Common.RetroativeEventUpdateConfirmMessage")
                    }
                    onClose={yes => {
                        if (yes) postData();
                        setDialog(null);
                    }}
                />
            );
        }
        else {
            postData();
        }
    };

    // Ao editar o DTO atual:
    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);
        setRecordChanged(isRecordChanged);
    }, [viewData]);

    useDidUpdateEffect(() => {
        if (viewData.startDate != loadDto?.startDate
            || viewData.endDate != loadDto?.endDate
            || !LibUtils.deepCompare(viewData.instructors, loadDto?.instructors)
            || viewData.bookAdditionalTimeAfter != loadDto?.bookAdditionalTimeAfter
            || viewData.bookAdditionalTimeBefore != loadDto?.bookAdditionalTimeBefore) {

            abortControllerRef.current?.abort();
            abortControllerRef.current = new AbortController();
            getInstructorsConflicts(api, viewData, abortControllerRef.current.signal, (e) => setViewError(e)).then(conflicts => setInstructorConflits(conflicts));
        }
    }, [viewData.startDate, viewData.endDate, viewData.instructors, viewData.bookAdditionalTimeAfter, viewData.bookAdditionalTimeBefore]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const tabIcon = (show: boolean) => {
        return show === true
            ?
            <MprcIconFa
                icon={faTriangleExclamation}
                color="warning"
                fixedWidth
                title={t("Meeting.Common.AlertInvalidValuesTooltip")}
            />
            :
            <div>&nbsp;</div>;
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getData() {
            try {
                if (meetingId != null) {
                    var result = await onGetData(meetingId, replacementComponentId);

                    const timezoneId = getMeetingTimezoneId(result.data);
                    if (timezoneId != null) {
                        result.data.startDate = TzUtils.applyTimezoneOffsetFromUtc(result.data.startDate, timezoneId);
                        result.data.endDate = TzUtils.applyTimezoneOffsetFromUtc(result.data.endDate, timezoneId);
                    }

                    setLoadDto(LibUtils.deepCopy(result.data));
                    setViewData(result.data);
                }
                else {

                    var data;
                    if (trainingId) {
                        const response = await api.v1.trainingManagement.getManagedMeetingDefault({ trainingId: trainingId });
                        data = getEmptyDto(props.meetingType, response.data);
                    }
                    else {
                        data = getEmptyDto(props.meetingType);
                    }

                    setLoadDto(LibUtils.deepCopy(data));
                    setViewData(data);
                }
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);


    ///
    const RenderTabs = useMemo(() => (
        <MprcBarTabs sx={{ marginBottom: 3 }}
            variant="fullWidth"
            value={currentTab}
            onChange={handleSelectTab}>
            {
                tabs.map((item) =>
                    <Tab key={item.value}
                        value={item.value}
                        label={item.name}
                        icon={tabIcon(currentTab != item.value && (item.hasError || (item.value == "instructors" && instructorConflits.length > 0)))}
                        iconPosition="end"
                    />)
            }
        </MprcBarTabs>
    ), [tabs, currentTab, instructorConflits]);

    ///
    const RenderContent = (
        <Grid container direction="column" height="100%" minHeight={260}>
            <Grid size="grow" width="100%">
                {!loading &&
                    <>
                        <EditMeetingWhenBox
                            mode={mode}
                            visible={currentTab == "when"}
                            viewData={
                                {
                                    endDate: viewData.endDate,
                                    location: viewData.location,
                                    meetingRoom: viewData.meetingRoom,
                                    meetingType: viewData.meetingType,
                                    meetingTypesEnabled: viewData.meetingTypesEnabled,
                                    multipleClasses: viewData.multipleClasses,
                                    onlineLocation: viewData.onlineLocation,
                                    startDate: viewData.startDate,
                                }
                            }
                            onFormUpdate={handleUpdateViewData}
                            onFormValidation={(valid, hasError) => {
                                setBoxValidations(state => ({ ...state, whenBox: valid }));
                                updateTabError("when", !valid || hasError);
                            }}
                            onError={error => resolveErrors(error)}
                            viewErrors={viewFieldsErrors}
                        />
                        <EditMeetingInstructorBox
                            mode={mode}
                            visible={currentTab == "instructors"}
                            viewData={
                                {
                                    assistants: viewData.assistants,
                                    bookAdditionalTimeAfter: viewData.bookAdditionalTimeAfter,
                                    bookAdditionalTimeBefore: viewData.bookAdditionalTimeBefore,
                                    endDate: viewData.endDate,
                                    instructors: viewData.instructors,
                                    meetingType: viewData.meetingType,
                                    multipleClasses: viewData.multipleClasses,
                                    startDate: viewData.startDate,
                                    instructorConflits
                                }
                            }
                            onFormUpdate={handleUpdateViewData}
                            onFormValidation={(valid, hasError) => {
                                setBoxValidations(state => ({ ...state, instructorsBox: valid }));
                                updateTabError("instructors", !valid || hasError);
                            }}
                            onError={error => resolveErrors(error)}
                            viewErrors={viewFieldsErrors}
                        />
                        <EditMeetingPresenceBox
                            visible={currentTab == "score"}
                            viewData={
                                {
                                    assignPresenceAfterViewRecording: viewData.assignPresenceAfterViewRecording,
                                    hasScore: viewData.hasScore,
                                    meetingType: viewData.meetingType,
                                    onlineLocation: viewData.onlineLocation
                                }
                            }
                            onFormUpdate={handleUpdateViewData}
                            onFormValidation={(valid, hasError) => {
                                setBoxValidations(state => ({ ...state, scoreBox: valid }));
                                updateTabError("score", !valid || hasError);
                            }}
                            viewErrors={viewFieldsErrors}
                        />
                    </>
                }
            </Grid>
        </Grid>
    );

    ///
    return (
        <>
            <Dialog
                open
                fullWidth
                maxWidth="lg"
                onClose={() => onClose(false)}
            >
                <MprcDialogTitleClose
                    shrinkPadding
                    typographyVariant="h4"
                    title={t(

                        viewData.meetingType === MeetingType.Presential
                            ? replacementComponentId != null ? "Meeting.Common.ClonePresentialTitle" : isNewRecord ? "Meeting.Common.NewPresentialTitle" : "Meeting.Common.EditPresentialTitle"
                            : replacementComponentId != null ? "Meeting.Common.CloneOnlineTitle" : isNewRecord ? "Meeting.Common.NewOnlineTitle" : "Meeting.Common.EditOnlineTitle"
                    )}
                    subtitle={
                        <MprcJoinComponents
                            components={[
                                viewData.trainingName,
                                mode == "classroom" && viewData.multipleClasses ? viewData.classroomName : null
                            ]}
                            separator="&bull;" />
                    }
                    onClose={() => onClose(false)}
                />
                <DialogContent>
                    {dialog}
                    {viewError && <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />}

                    <MprcRequestIndicator type={loading ? "request" : "post"} visible={loading || saving} />
                    <MprcContainer margin="none">

                        {
                            hasCloseEnrollments
                                ? <MprcNotice elevation="medium" items={[{ icon: faTriangleExclamationRegular, text: t("Meeting.MainBox.CloseEnrollmentsMessage") }]} />
                                : mode == "training" && viewData.multipleClasses && (
                                    <MprcNotice elevation="medium" items={[{ icon: faCircleInfo, text: t("Meeting.MainBox.MultipleClassesMessage") }]} />
                                )
                        }

                        {
                            loading
                                ? <EditMeeetingMainBoxSkeleton />
                                : <>
                                    <EditMeetingMainBox
                                        mode={mode}
                                        viewData={{
                                            name: viewData.name,
                                            description: viewData.description,
                                            thumbnail: viewData.thumbnail
                                        }}
                                        viewErrors={viewFieldsErrors}
                                        nameRef={nameRef}
                                        onFormUpdate={handleUpdateViewData}
                                        onFormValidation={(valid) => setBoxValidations(state => ({ ...state, mainBox: valid }))}
                                    />
                                    {RenderTabs}
                                    {RenderContent}
                                </>
                        }
                    </MprcContainer>
                </DialogContent>
                <DialogActions>
                    <ButtonCancel
                        variant="contained"
                        onClick={() => onClose(false)}
                    />
                    <ButtonSave
                        disabled={disabled || !canSave}
                        variant="contained"
                        onClick={handleSave}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
